/**
 * Backend /stream 엔드포인트 직접 호출 유틸리티
 *
 * voice_server WebSocket 대신 HTTP streaming 사용
 * - "..." 로딩 인디케이터가 정상 표시됨
 * - 응답이 실시간으로 스트리밍됨
 */

const STREAM_METADATA_MARKER = '[STREAM_METADATA]';

/**
 * Backend API URL 결정
 * NEXT_PUBLIC_API_URL 우선, 없으면 도메인에서 추론
 */
const getBackendApiUrl = (): string => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  if (apiUrl) return apiUrl;

  if (typeof window === 'undefined') return 'http://backend:8500';

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  if (hostname.includes('agentbuilder') && hostname.includes('nova.ggls.dev')) {
    if (hostname.startsWith('dev-agentbuilder')) {
      return `${protocol}//dev-agentbuilder-backend.nova.ggls.dev`;
    }
    if (hostname.startsWith('agentbuilder-staging')) {
      return `${protocol}//agentbuilder-staging-backend.nova.ggls.dev`;
    }
    return `${protocol}//agentbuilder-backend.nova.ggls.dev`;
  }

  // kikiz.ai 도메인 (agentbuilder.nova.ggls.dev의 AWS Amplify 별칭)
  if (hostname === 'kikiz.ai' || hostname.endsWith('.kikiz.ai')) {
    return `${protocol}//agentbuilder-backend.nova.ggls.dev`;
  }

  if (hostname === 'localhost') {
    return 'http://localhost:8500';
  }

  return `${protocol}//${hostname}:8500`;
};

export interface StreamChatOptions {
  agentId: string;
  message: string;
  sessionId?: string;
  language?: string;
  displayMode?: string;
  userId?: string;
}

export interface StreamChatCallbacks {
  onChunk: (accumulatedText: string, isFirstChunk: boolean) => void;
  onComplete: (fullText: string, metadata?: any) => void;
  onError: (error: Error) => void;
}

export async function streamChat(
  options: StreamChatOptions,
  callbacks: StreamChatCallbacks,
): Promise<void> {
  const {
    agentId,
    message,
    sessionId,
    language = 'ko',
    displayMode = 'web',
    userId = 'public_expo_user',
  } = options;

  const backendUrl = getBackendApiUrl();

  try {
    const response = await fetch(`${backendUrl}/api/agents/${agentId}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        message,
        session_id: sessionId || '',
        language,
        display_mode: displayMode,
      }),
    });

    if (!response.ok) {
      throw new Error(`Streaming failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('ReadableStream not supported');
    }

    const decoder = new TextDecoder();
    let fullText = '';
    let isFirstChunk = true;
    let metadata: any = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });

      if (chunk.includes(STREAM_METADATA_MARKER)) {
        const parts = chunk.split(STREAM_METADATA_MARKER);
        const textPart = parts[0];
        const metadataPart = parts[1];

        if (textPart) {
          fullText += textPart;
          callbacks.onChunk(fullText.trim(), isFirstChunk);
          isFirstChunk = false;
        }

        if (metadataPart) {
          try {
            metadata = JSON.parse(metadataPart.trim());
          } catch {
            // metadata parse failure, ignore
          }
        }
      } else if (chunk.trim()) {
        fullText += chunk;
        callbacks.onChunk(fullText.trim(), isFirstChunk);
        isFirstChunk = false;
      }
    }

    callbacks.onComplete(fullText.trim(), metadata);
  } catch (error) {
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
  }
}
